import React, { useRef } from 'react'
import { motion } from 'framer-motion'
import { useIntersectionObserver } from '@hooks/useIntersectionObserver'
import { APP_CONFIG } from '@config/index'
import PageQrCard from '@components/ui/PageQrCard'
import { useResponsiveSettings } from '@hooks/useResponsiveSettings'

const Contact: React.FC = () => {
    const sectionRef = useRef<HTMLElement>(null)
    const entry = useIntersectionObserver(sectionRef, { threshold: 0.1 })
    const isVisible = !!entry?.isIntersecting
    const { shouldReduceEffects } = useResponsiveSettings()

    const contactInfo = [
        {
            icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
            ),
            title: 'Dirección',
            content: APP_CONFIG.contact.address
        },
        {
            icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
            ),
            title: 'Teléfono',
            content: APP_CONFIG.contact.phone
        },
        {
            icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            ),
            title: 'Horarios',
            content: (
                <div>
                    <div>{APP_CONFIG.contact.hours.weekdays}</div>
                    <div>{APP_CONFIG.contact.hours.saturday}</div>
                    <div className="text-sm text-gray-500">Dom: Cerrado</div>
                </div>
            )
        },
        {
            icon: (
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
            ),
            title: 'Síguenos',
            content: (
                <div className="flex space-x-4">
                    <a
                        href={APP_CONFIG.social.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-700 transition-colors flex items-center gap-2"
                    >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                        </svg>
                        Facebook
                    </a>
                </div>
            )
        }
    ]

    return (
        <section
            ref={sectionRef}
            id="contacto"
            className="section-padding bg-white"
        >
            <div className="container-fluid px-4 sm:px-6">
                <motion.div
                    className="text-center mb-12 sm:mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                    transition={{ duration: 0.8 }}
                >
                    <h2 className="section-title mb-4 sm:mb-6">
                        Visítanos en
                        <span className="block text-gradient">Toluca, México</span>
                    </h2>
                    <p className="section-subtitle px-4">
                        Estamos para servirte con la mejor atención
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16">
                    {/* Contact Information */}
                    <motion.div
                        className="space-y-6 sm:space-y-8"
                        initial={{ opacity: 0, x: -50 }}
                        animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
                        transition={{ duration: 0.8 }}
                    >
                        {contactInfo.map((info, index) => (
                            <motion.div
                                key={index}
                                className="flex items-start space-x-3 sm:space-x-4"
                                initial={{ opacity: 0, y: 20 }}
                                animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                            >
                                <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center">
                                    {info.icon}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1 sm:mb-2">
                                        {info.title}
                                    </h3>
                                    <div className="text-sm sm:text-base text-gray-600">
                                        {info.content}
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>

                    {/* Location Image and Map Link */}
                    <motion.div
                        className="relative space-y-4 sm:space-y-6"
                        initial={{ opacity: 0, x: 50 }}
                        animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        <PageQrCard url={APP_CONFIG.site.url} />

                        {/* Imagen de la Óptica */}
                        <div className="rounded-2xl overflow-hidden shadow-lg h-48 sm:h-64 relative group">
                            <img
                                src="/fotoOptica.jpg"
                                alt="Grupo Médico Empresarial - Óptica"
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-gray-900/30 to-transparent flex items-end p-4 sm:p-6">
                                <div className="text-white">
                                    <h4 className="text-xl sm:text-2xl font-bold mb-1 sm:mb-2">Nuestras Instalaciones</h4>
                                    <p className="text-xs sm:text-sm text-gray-200">Visítanos en Toluca</p>
                                </div>
                            </div>
                        </div>

                        {/* Botón para Ver Mapa */}
                        <motion.a
                            href="https://maps.app.goo.gl/g8WBHXCy9oh2cCWd8"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block relative rounded-2xl overflow-hidden shadow-lg h-64 sm:h-80 group cursor-pointer"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            {/* Fondo con gradiente */}
                            <div className="absolute inset-0 bg-gradient-to-br from-primary-600 via-primary-700 to-accent-600">
                                {/* Patrón de puntos */}
                                <div className="absolute inset-0 opacity-20">
                                    <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                                        <defs>
                                            <pattern id="dot-pattern" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                                                <circle cx="2" cy="2" r="1" fill="white" />
                                            </pattern>
                                        </defs>
                                        <rect width="100%" height="100%" fill="url(#dot-pattern)" />
                                    </svg>
                                </div>
                            </div>

                            {/* Contenido */}
                            <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-6 sm:p-8 text-center">
                                <motion.div
                                    className="mb-4 sm:mb-6"
                                    animate={shouldReduceEffects ? undefined : {
                                        y: [0, -10, 0],
                                    }}
                                    transition={shouldReduceEffects ? undefined : {
                                        duration: 2,
                                        repeat: Infinity,
                                        ease: 'easeInOut'
                                    }}
                                >
                                    <svg className="w-16 h-16 sm:w-20 sm:h-20 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                </motion.div>

                                <h4 className="text-2xl sm:text-3xl font-black mb-2 sm:mb-3 drop-shadow-lg">
                                    Ver Ubicación en Google Maps
                                </h4>
                                <p className="text-sm sm:text-base text-white/90 mb-4 sm:mb-6 max-w-md">
                                    Júpiter 125, Colonia Meteoro<br />
                                    Toluca, México CP 50070
                                </p>

                                <motion.div
                                    className="btn-primary bg-white text-primary-600 hover:bg-gray-100 inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base font-bold"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                    Abrir en Maps
                                </motion.div>
                            </div>

                            {/* Efecto de hover */}
                            <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300" />
                        </motion.a>
                    </motion.div>
                </div>
            </div>
        </section>
    )
}

export default Contact